import Vue from 'vue'
import VueRouter, { RouteConfig, Route } from 'vue-router'

import Home from '@/views/Home.vue'
import TasksView from '@/views/TasksView.vue'
import NotFoundView from '@/views/NotFoundView.vue'
import GroupCreationView from '@/views/GroupCreationView.vue'
import GroupEditView from '@/views/GroupEditView.vue'
import CallCentreView from '@/views/CallCentreView.vue'
import GroupsView from '@/views/GroupsView.vue'
import AlarmsView from '@/views/AlarmsView.vue'
import CasesView from '@/views/CasesView.vue'
import PollingView from '@/views/PollingView.vue'
import PollingLocationView from '@/views/PollingLocationView.vue'
import BuildingsView from '@/views/BuildingsView.vue'
import NewBuildingView from '@/views/NewBuildingView.vue'

import { Authentication } from '@/logic/common'
import { screenPermissions } from '@/logic/common/Permissions'
import { actions } from '@/store'
import request from '@/requests'

import {
  SCREEN_CLIENTS,
  SCREEN_GROUP_EDIT,
  SCREEN_HOME,
  SCREEN_TASKS,
  SCREEN_NOT_FOUND,
  SCREEN_CALL_CENTRE,
} from '@/constants'
import PurchaseOrderView from '@/views/PurchaseOrderView.vue'
import BuildingDetailView from '@/views/BuildingDetailView.vue'
import PartnerDetailView from '@/views/PartnerDetailView.vue'

Vue.use(VueRouter)

const routes: Array<RouteConfig> = [
  {
    path: '/',
    name: SCREEN_HOME,
    component: Home,
  },
  {
    path: '/clients',
    name: SCREEN_CLIENTS,
    component: GroupCreationView,
    meta: {
      permissions: screenPermissions.clients,
    },
  },
  {
    path: '/clients/group/:name',
    name: SCREEN_GROUP_EDIT,
    component: GroupEditView,
    meta: {
      permissions: screenPermissions.groupEdit,
    },
  },

  {
    path: '/tasks',
    name: SCREEN_TASKS,
    component: TasksView,
    meta: {
      permissions: screenPermissions.tasks,
    },
  },
  {
    path: '/call-centre',
    name: SCREEN_CALL_CENTRE,
    component: CallCentreView,
    meta: {
      permissions: screenPermissions.callCentre,
    },
  },
  {
    path: '/groups',
    name: 'view-groups',
    component: GroupsView,
    meta: {
      permissions: screenPermissions.clients,
    },
  },
  {
    path: '/v2/alarms',
    name: 'alarms',
    component: AlarmsView,
    meta: {
      permissions: 'Eleena:view_alarms',
    },
  },
  {
    path: '/v2/cases',
    name: 'cases',
    component: CasesView,
    props: { showModal: false, address: '', alarmId: null },
    meta: {
      permissions: 'Tickets:view_cases',
    },
  },
  {
    path: '/v2/cases/:id',
    name: 'case-detail',
    component: CasesView,
    meta: {
      permissions: 'Tickets:view_cases',
    },
  },
  {
    path: '/v2/polling',
    name: 'polling',
    component: PollingView,
    meta: {
      permissions: 'ERP:view_polling',
    },
  },
  {
    path: '/v2/polling/location/:locationId',
    name: 'polling-location',
    component: PollingLocationView,
    meta: {
      permissions: 'ERP:view_polling',
    },
  },
  {
    path: '/v2/polling/:id',
    name: 'polling-detail',
    component: PollingView,
    meta: {
      permissions: 'ERP:view_polling',
    },
  },
  {
    path: '/purchase-orders',
    name: 'purchase-orders',
    component: PurchaseOrderView,
    meta: {
      permissions: 'ERP:view_purchase_orders',
    },
  },
  {
    path: '/purchase-orders/:id',
    name: 'purchase-order-detail',
    component: PurchaseOrderView,
    meta: {
      permissions: 'ERP:view_purchase_orders',
    },
  },
  {
    path: '/partners/:id',
    name: 'partner-detail',
    component: PartnerDetailView,
    meta: {
      permissions: 'ERP:view_partner_details',
    },
  },
  {
    path: '/v2/buildings',
    name: 'buildings',
    component: BuildingsView,
    meta: {
      permissions: 'ERP:view_buildings',
    },
  },
  {
    path: '/v2/buildings/:id',
    name: 'building-details',
    component: BuildingDetailView,
    meta: {
      permissions: 'ERP:view_buildings',
    },
  },
  {
    path: '/v2/buildings/new',
    name: 'new-building',
    component: NewBuildingView,
    meta: {
      permissions: 'ERP:view_buildings',
    },
  },
  {
    path: '*', // catch-all route - 404
    name: SCREEN_NOT_FOUND,
    component: NotFoundView,
  },
]

const router = new VueRouter({ routes })

const auth = new Authentication(router)

router.beforeEach(async (to: Route, _from: Route, next) => {
  if (to.name !== SCREEN_TASKS) return next()

  const address = to.query.address
  const hasSelection = Boolean(to.query.woco && to.query.project && to.query.group)

  if (typeof address !== 'string' || hasSelection) return next()

  const getAddressLookupVariants = (rawAddress: string) => {
    const cleaned = rawAddress.trim().replace(/\s+/g, ' ')
    const postalCodeNoSpace = cleaned.replace(/(\d{4})\s+([a-zA-Z]{2})/g, '$1$2')
    return Array.from(new Set([rawAddress, cleaned, postalCodeNoSpace]))
  }

  try {
    const addressVariants = getAddressLookupVariants(address)

    let response: any = null
    for (const variant of addressVariants) {
      // eslint-disable-next-line no-await-in-loop
      response = await request.groups.getAddressDetails(variant)
      if (response?.status === 'OK') break
    }
    if (response?.status !== 'OK') return next()

    const groups = response?.data?.groups
    if (!Array.isArray(groups) || groups.length === 0) return next()

    const canonicalAddress =
      typeof response?.data?.address === 'string' && response.data.address.trim()
        ? response.data.address
        : address

    const detail = to.query.detail
    const panes = to.query.panes

    const panesToTaskType: { [key: string]: string } = {
      DesignPane: 'design',
      InstallationPane: 'installation',
      SalesPane: 'sales',
      InspectionPane: 'inspection',
      ClientPane: 'client',
    }

    const targetTaskTypeFromDetail =
      typeof detail === 'string' && detail.trim() ? detail.toLowerCase() : null

    const targetTaskTypeFromPanes =
      typeof panes === 'string'
        ? panes
            .split(',')
            .map((p) => p.trim())
            .map((p) => panesToTaskType[p])
            .find((t) => typeof t === 'string')
        : null

    const targetTaskType = targetTaskTypeFromDetail || targetTaskTypeFromPanes

    const taskTypeToPane: { [key: string]: string } = {
      design: 'DesignPane',
      installation: 'InstallationPane',
      sales: 'SalesPane',
      inspection: 'InspectionPane',
      client: 'ClientPane',
    }

    // apps arrive as {id, name} objects from the groups service
    const groupHasApp = (g: any) =>
      Array.isArray(g?.apps) &&
      g.apps.some((app: any) => (typeof app === 'string' ? app : app?.name) === targetTaskType)

    // A multi-project address has groups of several projects: stay within the
    // project the deeplink asks for, falling back to all groups if it has none
    const queryProject = typeof to.query.project === 'string' ? to.query.project : null
    const projectGroups = queryProject
      ? groups.filter((g: any) => g?.project === queryProject)
      : []
    const candidateGroups = projectGroups.length > 0 ? projectGroups : groups

    const selectedGroup =
      (targetTaskType ? candidateGroups.find(groupHasApp) : null) || candidateGroups[0]

    if (!selectedGroup?.woco || !selectedGroup?.project || !selectedGroup?.name) return next()

    const nextQuery: any = {
      ...to.query,
      address: canonicalAddress,
      woco: selectedGroup.woco,
      project: selectedGroup.project,
      group: selectedGroup.name,
    }

    if (targetTaskType && taskTypeToPane[targetTaskType]) {
      // keep every valid pane the deeplink asked for, not just the detail's pane
      const incomingPanes =
        typeof panes === 'string'
          ? panes
              .split(',')
              .map((p) => p.trim())
              .filter((p) => panesToTaskType[p] !== undefined)
          : []
      nextQuery.panes = Array.from(
        new Set([...incomingPanes, taskTypeToPane[targetTaskType]])
      ).join(',')
      nextQuery.detail = targetTaskType
    }

    return next({ path: to.path, query: nextQuery, hash: to.hash, replace: true })
  } catch (e) {
    return next()
  }
})

// eslint-disable-next-line @typescript-eslint/no-unused-vars
router.afterEach((_to: Route, _from: Route) => {
  auth.updateStatus()
  document.title = `${_to.name} | ERP | NORM` || 'ERP | NORM'
  actions.common.commitNotifications([])
})

export default router
